import { HolidayDto } from '@ecdlink/core';
import {
  addDays,
  getDayOfYear,
  getYear,
  intervalToDuration,
  isBefore,
  isSameDay,
  isWeekend,
  parseISO,
  getQuarter,
} from 'date-fns';

const dateLongMonthOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

export const isWorkingDay = (date: Date, holidays: HolidayDto[]) => {
  const holidaysAsDaysOfYears = holidays.map((x) =>
    getDayOfYear(parseISO(x.day))
  );

  const dateDayOfYear = getDayOfYear(date);

  if (isWeekend(date)) return false;

  if (holidaysAsDaysOfYears.includes(dateDayOfYear)) return false;

  return true;
};

export const getDatesForRange = (
  startDate: number | Date,
  endDate: number | Date
): Date[] => {
  let now = startDate,
    dates: Date[] = [];

  while (isBefore(now, endDate) || isSameDay(now, endDate)) {
    dates.push(new Date(now));
    now = addDays(now, 1);
  }
  return dates;
};

export const isDayInThePast = (dateToCompare: Date, date: Date) => {
  const [dateToCompareDayOfYear, dateDayOfyear, dateToCompareYear, dateYear] = [
    getDayOfYear(dateToCompare),
    getDayOfYear(date),
    getYear(dateToCompare),
    getYear(date),
  ];

  return (
    dateToCompareDayOfYear < dateDayOfyear && dateToCompareYear <= dateYear
  );
};

/**
 * A child's date of birth is NOT NULL in the database, so a child created before their date of
 * birth has been captured - the caregiver-link flow creates one as soon as the link is sent - has
 * to be stored with a placeholder. mapChildUserDto produced one by accident: with no date captured
 * it evaluated `new Date(dobYear ?? 0, (dobMonth ?? 1) - 1, dobDay ?? 0)`, and JavaScript maps
 * years 0-99 onto 1900 and day 0 onto the last day of the previous month, giving 31 December 1899.
 *
 * Resuming such a child then read that placeholder back into the date of birth field, so the field
 * arrived already filled in and the child was measured as over a century old - reported as "older
 * than 10". Anything before the cut-off is a placeholder, never a real date of birth.
 */
export const UNCAPTURED_DATE_OF_BIRTH = new Date(1899, 11, 31);

const UNCAPTURED_DATE_OF_BIRTH_CUTOFF = new Date(1900, 0, 1);

export const isCapturedDateOfBirth = (
  value?: Date | string | null
): boolean => {
  if (!value) return false;

  const date = value instanceof Date ? value : new Date(value);

  return !isNaN(date.getTime()) && date >= UNCAPTURED_DATE_OF_BIRTH_CUTOFF;
};

export const calculateFullAge = (dob: Date) => {
  const { years, months, days } = intervalToDuration({
    start: dob,
    end: new Date(),
  });
  return { years: years ?? 0, months: months ?? 0, days: days ?? 0 };
};

export const formatDateLong = (date: Date) => {
  return new Date(date).toLocaleDateString('en-ZA', dateLongMonthOptions);
};

export const getQuarterMonths = (date: Date) => {
  const quarter = getQuarter(date);
  if (quarter === 1) {
    return 'Jan to March';
  }
  if (quarter === 2) {
    return 'April to June';
  }
  if (quarter === 3) {
    return 'July to September';
  } else {
    return 'October to December';
  }
};

// set to midnight local time
export function normalizeDateToMidnight(date: string | Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Create UTC midnight date (no timezone bugs)
export function toUtcMidnightDate(date: Date | null): Date | undefined {
  if (!date) return undefined;
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
  );
}

// Dropdown for months, formatted for backend
export function getMonthsList(): { label: string; value: string }[] {
  const result: { label: string; value: string }[] = [];
  const today = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const label = date.toLocaleString('default', {
      month: 'short',
      year: 'numeric',
    });
    result.push({
      label,
      value: date.toISOString().split('T')[0] + 'T00:00:00',
    });
  }
  return result;
}
